import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { groupBy, orderBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router';

import type { GenericChartDataType } from '@/components/charts/charts.types';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import KeyValueResponsive from '@/components/KeyValueResponsive';
import LoadingIndicator from '@/components/LoadingIndicator';
import WorldMapChart from '@/components/WorldMapChart/WorldMapChart';
import { PRODUCTS } from '@/constants/products.constants';
import AssetAlerts from '@/features/AssetAlerts';
import AssetConnectivityTable from '@/features/AssetConnectivityTable';
import AssetPanel, { dataProviderDetailsFields } from '@/features/AssetPanel';
import AssetProviderLogo from '@/features/AssetProviderLogo';
import AssetRiskScoreDialog from '@/features/AssetRiskScoreDialog';
import AssetSubTopbarBottom from '@/features/AssetSubTopbarBottom';
import AssetSubTopbarTop from '@/features/AssetSubTopbarTop';
import AssetVulnerabilitiesTable from '@/features/AssetVulnerabilitiesTable';
import MainContent from '@/layout/MainContent';

import {
  ASSET_DATA_PROVIDER_LABELS,
  type AssetConnectionMetricsDTO,
  type AssetExternalDetailDTO,
  assetRiskScoreDialogAtom,
  selectedAssetAtom,
  selectedAssetIdAtom,
  useFetchAssetAlerts,
  useFetchAssetAlertsPerDay,
  useFetchAssetConnections,
  useFetchAssetDetails,
  useFetchAssetRemediations
} from '../Assets';
import { getDates } from '../Dashboard/Dashboard.utils';
import {
  organizationsAtom,
  selectedOrganizationIdAtom
} from '../Organizations';
import { REMEDIATIONS_BAR_SERIES } from '../VulnerabilityManagement/VulnerabilityManagement.constants';
import useStyles from './Asset.styles';

const Asset = () => {
  const { classes } = useStyles();
  const { id } = useParams();
  const [, setSelectedAssetId] = useAtom(selectedAssetIdAtom);
  const [selectedOrganizationId, setSelectedOrganizationId] = useAtom(
    selectedOrganizationIdAtom
  );
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [assetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);

  const { isLoading: isLoadingDetails, data: assetDetails } =
    useFetchAssetDetails();
  const { data: selectedAssetConnections } = useFetchAssetConnections();
  const { hasData: hasAlerts } = useFetchAssetAlerts();
  const { hasData: hasAlertsPerDayData } = useFetchAssetAlertsPerDay();
  const { isLoading: isLoadingRemediations, data: assetRemediations } =
    useFetchAssetRemediations(Number(id) || 0);

  const hasVMPE = organizations
    .find((org) => org.id === selectedAsset?.organization_id)
    ?.product_type.includes(PRODUCTS.VMPE);

  useEffect(() => {
    if (id) {
      setSelectedAssetId(Number(id));
    }
  }, [id]);

  // if the selected asset's organization_id changes and is different from the current selectedOrganizationId, update the selected organization
  useEffect(() => {
    if (
      selectedAsset?.organization_id !== selectedOrganizationId &&
      selectedOrganizationId !== 0
    ) {
      setSelectedOrganizationId(selectedAsset?.organization_id as number);
    }
  }, [selectedAsset]);
  const remediationsData = useMemo(() => {
    if (!assetRemediations || assetRemediations.length === 0) return [];

    const remediationsDates = getDates(dayjs(), 30);

    const groupedRemediations = groupBy(assetRemediations, (remediation) =>
      dayjs.utc(remediation.date_remediated).local().format('YYYY-MM-DD')
    );

    const remediations: GenericChartDataType[] = [];
    remediationsDates?.forEach((date) => {
      const dateKey = dayjs(date).format('YYYY-MM-DD');
      remediations.push({
        date: dayjs(date).toDate(),
        count: groupedRemediations[dateKey]?.length || 0
      });
    });
    return remediations;
  }, [assetRemediations]);

  const topAndBottomConnections = useMemo(() => {
    if (!selectedAssetConnections) return [];
    const sortedConnections = orderBy(
      selectedAssetConnections,
      'count',
      'desc'
    ) as AssetConnectionMetricsDTO[];

    if (sortedConnections.length <= 10) return sortedConnections;

    const topConnections = sortedConnections.slice(0, 5);
    const bottomConnections = sortedConnections.slice(-5);
    return [...topConnections, ...bottomConnections];
  }, [selectedAssetConnections]);

  if (Number.isNaN(Number(id))) {
    return <Navigate to="/asset-not-found" replace />;
  }

  const externalDetails =
    selectedAsset?.external_details || ([] as AssetExternalDetailDTO[]);

  // This prevents the page from flashing when the page is reloaded
  if (isLoadingDetails || assetDetails === undefined) {
    return <LoadingIndicator isLoading={isLoadingDetails} />;
  }

  return (
    <>
      <div>
        <LoadingIndicator isLoading={isLoadingDetails} />
        <AssetSubTopbarTop />
        <AssetSubTopbarBottom />
        <MainContent>
          <Stack className={classes.asset} direction="column" spacing={3.5}>
            <Stack
              direction={{ md: 'row-reverse' }}
              className={classes.assetTopContainer}
              spacing={3.5}
            >
              <Stack
                sx={{ display: 'flex', flex: 1 }}
                spacing={4}
                className={classes.assetSidebarContainer}
                direction="column"
              >
                <Box sx={{ flexGrow: 1 }}>
                  <AssetPanel />
                </Box>
                {hasVMPE && (
                  <Box className={classes.assetRemediationTableContainer}>
                    <GenericChartWidget
                      title="Remediations Over Time"
                      loading={isLoadingRemediations}
                    >
                      <VerticalBarChart
                        data={remediationsData}
                        series={REMEDIATIONS_BAR_SERIES}
                      />
                    </GenericChartWidget>
                  </Box>
                )}
              </Stack>
              <Stack direction="column" sx={{ width: '100%' }} spacing={3.5}>
                {externalDetails?.map((item) => (
                  <Card
                    key={item.provider}
                    className={classes.externalDetailsBox}
                  >
                    <CardHeader
                      className={classes.externalDetailsCardHeader}
                      title={
                        <Stack
                          direction="row"
                          sx={{ alignItems: 'center', display: 'flex' }}
                        >
                          <AssetProviderLogo label={item.provider} />
                          <Typography className={classes.assetSectionTitle}>
                            {
                              ASSET_DATA_PROVIDER_LABELS[
                                item.provider as keyof typeof ASSET_DATA_PROVIDER_LABELS
                              ]
                            }
                          </Typography>
                        </Stack>
                      }
                    />
                    <CardContent>
                      <KeyValueResponsive
                        items={dataProviderDetailsFields(
                          item as AssetExternalDetailDTO
                        )}
                      />
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
            {(hasAlerts || hasAlertsPerDayData) && <AssetAlerts />}
            {hasVMPE && (
              <div className={classes.vulnerabilitiesTableContainer}>
                <Box className={classes.assetTableHeader}>
                  <span className={classes.assetSectionTitle}>
                    Asset Vulnerabilities
                  </span>
                </Box>
                <Box className={classes.assetVulnerabilitiesTableContainer}>
                  <AssetVulnerabilitiesTable assetId={Number(id) || 0} />
                </Box>
              </div>
            )}
            <Box className={classes.assetConnectivitySection}>
              <Box className={classes.assetConnectivitySectionHeader}>
                <Typography className={classes.assetSectionTitle}>
                  Highest and Lowest Asset Connections
                </Typography>
              </Box>
              <Stack
                sx={{ alignItems: 'stretch' }}
                direction={{ md: 'column', lg: 'row' }}
                spacing={0}
              >
                <div className={classes.worldMapChartContainer}>
                  <WorldMapChart
                    data={
                      topAndBottomConnections as AssetConnectionMetricsDTO[]
                    }
                  />
                </div>
                <div className={classes.assetConnectivityTableWrapper}>
                  <AssetConnectivityTable
                    selectedAssetConnections={
                      topAndBottomConnections as AssetConnectionMetricsDTO[]
                    }
                  />
                </div>
              </Stack>
            </Box>
          </Stack>
        </MainContent>
      </div>
      {assetRiskScoreDialog && <AssetRiskScoreDialog />}
    </>
  );
};

export default Asset;
